import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { TEST_IDS } from "@/constants/testIds";

const COLLECTION_LINKS = [
  { label: "Tops", handle: "tops" },
  { label: "Bottoms", handle: "bottoms" },
  { label: "Swimwear", handle: "swimwear" },
  { label: "Shoes", handle: "shoes" },
  { label: "Accessories", handle: "accessories" },
  { label: "Shapewear", handle: "shapewear" },
  { label: "Kids", handle: "kids" },
  { label: "Home & Decor", handle: "home-decor" },
  { label: "Drinkware", handle: "drinkware" },
  { label: "Bags & Travel", handle: "bags-travel" },
  { label: "Stationery", handle: "stationery" },
];

export default function Navbar() {
  const { totalItems, setIsOpen } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const goTo = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  return (
    <header data-testid={TEST_IDS.NAV.root} className="fixed top-0 left-0 right-0 z-50 glass-nav border-b border-plum/5">
      {/* Top bar — logo + icons */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-14 lg:h-16">
        <Link to="/" data-testid={TEST_IDS.NAV.logo} className="flex items-center gap-1 group shrink-0">
          <span className="font-serif text-xl lg:text-2xl font-semibold text-plum tracking-tight">
            You Belong Club
          </span>
          <span className="w-2 h-2 rounded-full bg-amber inline-block transition-transform duration-300 group-hover:scale-150" />
        </Link>

        <div className="flex items-center gap-3">
          <button className="p-2 text-plum hover:text-amber transition-colors duration-200" aria-label="Search" data-testid="nav-search-icon">
            <Search className="w-5 h-5" />
          </button>
          <button
            data-testid={TEST_IDS.NAV.cartButton}
            onClick={() => setIsOpen(true)}
            className="relative p-2 text-plum hover:text-amber transition-colors duration-300"
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {totalItems > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-amber text-white text-xs font-bold rounded-full flex items-center justify-center"
                data-testid={TEST_IDS.CART.itemCount}
              >
                {totalItems}
              </motion.span>
            )}
          </button>
          <button
            data-testid={TEST_IDS.NAV.mobileMenu}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-plum"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Desktop collection links bar */}
      <nav
        className="hidden lg:block border-t border-plum/5"
        data-testid="nav-collection-bar"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-6 h-10 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            {COLLECTION_LINKS.map((link) => (
              <Link
                key={link.handle}
                to={`/collection/${link.handle}`}
                data-testid={`nav-collection-${link.handle}`}
                className="text-xs font-medium text-plum hover:text-amber whitespace-nowrap transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
            <span className="w-px h-4 bg-plum/10 shrink-0" />
            <button
              onClick={() => goTo("/#our-standard")}
              data-testid="nav-our-standard"
              className="text-xs font-medium text-plum hover:text-amber whitespace-nowrap transition-colors duration-200"
            >
              Our Standard
            </button>
            <button
              onClick={() => goTo("/#about")}
              data-testid="nav-about"
              className="text-xs font-medium text-plum hover:text-amber whitespace-nowrap transition-colors duration-200"
            >
              About
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-cream border-t border-plum/5"
          >
            <div className="px-6 py-4 max-h-[75vh] overflow-y-auto space-y-0.5">
              {COLLECTION_LINKS.map((link) => (
                <button
                  key={link.handle}
                  onClick={() => goTo(`/collection/${link.handle}`)}
                  className="block w-full text-left px-2 py-2.5 text-sm font-medium text-plum hover:bg-soft-pink/50 rounded-lg transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <div className="h-px bg-plum/5 my-2" />
              <button onClick={() => goTo("/#our-standard")} className="block w-full text-left px-2 py-2.5 text-sm font-medium text-plum hover:bg-soft-pink/50 rounded-lg">
                Our Standard
              </button>
              <button onClick={() => goTo("/#about")} className="block w-full text-left px-2 py-2.5 text-sm font-medium text-plum hover:bg-soft-pink/50 rounded-lg">
                About
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
